import { Link } from "react-router-dom";
import { ArrowDown, Star } from "lucide-react";
import { MainCalculator } from "../components/MainCalculator";
import { SubCalculators } from "../components/SubCalculators";
import { AdSlot } from "../components/AdSlot";
import { ArticlesGrid } from "../components/ArticlesGrid";
import { FAQ } from "../components/FAQ";
import { useSeo } from "../lib/useSeo";

const stats = [
    { value: "75%", label: "Of adults are chronically dehydrated" },
    { value: "2%", label: "Drop in body water = 10% lower performance" },
    { value: "30-40", label: "ml per kg of body weight, base intake" },
    { value: "0", label: "ML wasted. Science-backed precision." },
];

export const Home = () => {
    useSeo({
        title: "Daily Water Intake Calculator (Free)",
        description:
            "Free Daily Water Intake Calculator. Get a personalized hydration target by body weight, activity, climate and life stage. Imperial or metric, no sign-up.",
        path: "/",
    });

    return (
        <>
            {/* HERO */}
            <section className="border-b border-foreground" data-testid="home-hero">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 pt-12 sm:pt-20 pb-12">
                    <div className="flex items-center gap-3 mb-6">
                        <span className="inline-flex items-center gap-1 border border-foreground bg-card px-3 py-1.5 text-[10px] font-mono uppercase tracking-[0.2em]">
                            <Star className="h-3 w-3 fill-primary text-primary" /> Free · No accounts
                        </span>
                    </div>
                    <h1
                        className="text-5xl sm:text-7xl lg:text-8xl font-black tracking-tighter uppercase leading-[0.95] mb-6 max-w-5xl"
                        data-testid="hero-h1"
                    >
                        Daily Water<br />
                        <span className="text-primary">Intake</span><br />
                        Calculator.
                    </h1>
                    <p className="text-lg sm:text-xl text-foreground/80 max-w-3xl mb-6 leading-relaxed" data-testid="hero-intro">
                        Get your personalized daily water target in seconds — 
based on your weight, activity, climate and age.
                    </p>
                    <div className="flex flex-wrap gap-3 items-center">
                        <a href="#calculator" data-testid="cta-calculate" className="btn-primary-editorial">
                            Calculate now <ArrowDown className="h-4 w-4 ml-2" />
                        </a>
                        <Link to="/calculators" data-testid="cta-more-tools" className="btn-outline-editorial">
                            More calculators
                        </Link>
                    </div>
                </div>

                {/* Stats strip */}
                <div className="border-t border-foreground bg-foreground text-background">
                    <div className="max-w-7xl mx-auto grid grid-cols-2 lg:grid-cols-4">
                        {stats.map((s, i) => (
                            <div
                                key={i}
                                className={`p-6 sm:p-8 ${i < 3 ? "border-r border-background/20" : ""} ${i < 2 ? "border-b lg:border-b-0 border-background/20" : ""}`}
                                data-testid={`stat-${i}`}
                            >
                                <div className="text-3xl sm:text-4xl font-black tracking-tighter mb-2">
                                    {s.value}
                                </div>
                                <div className="text-xs font-mono uppercase tracking-widest text-background/60 leading-relaxed">
                                    {s.label}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* AdSense slot #1 — below H1, before calculator */}
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 pt-8">
                <AdSlot slot="home-top" height={120} testId="ad-home-top" />
            </div>

            {/* CALCULATOR */}
            <section className="py-12 sm:py-16" id="calculator">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10">
                    <div className="mb-10">
                        <div className="editorial-label mb-3">Daily water intake</div>
                        <h2 className="text-4xl sm:text-6xl font-black tracking-tighter max-w-3xl">
                            Inputs left. Outputs right.
                        </h2>
                    </div>
                    <MainCalculator />
                    <p className="text-xs font-mono uppercase tracking-widest text-foreground/50 mt-6 max-w-2xl">
                        Estimates based on common clinical guidelines (35 ml/kg baseline, IOM
                        pregnancy &amp; ACSM workout adjustments). Not medical advice.
                    </p>
                </div>
            </section>

            {/* AdSense slot #2 — below calculator result */}
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10">
                <AdSlot slot="home-mid" height={120} testId="ad-home-mid" />
            </div>

            {/* SUB CALCULATORS */}
            <section className="py-16 sm:py-24 border-t border-foreground">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10">
                    <div className="mb-10">
                        <div className="editorial-label mb-3">More calculators</div>
                        <h2 className="text-4xl sm:text-5xl font-black tracking-tighter max-w-3xl">
                            Tuned for every situation.
                        </h2>
                    </div>
                    <SubCalculators />
                </div>
            </section>

            <ArticlesGrid limit={3} heading="Hydration field notes." />

            <FAQ />

            {/* AdSense slot #3 — below FAQ */}
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10">
                <AdSlot slot="home-faq" height={120} testId="ad-home-faq" />
            </div>
        </>
    );
};
